/**
 * @file src/local/tags/readAudioTags.js
 * @description What a file says about itself: a title, an artist, an album, and a cover.
 *
 * A local file used to be shown by its file name and nothing else, because nothing was read from
 * it but its audio. Most files carry more than that, and a card for somebody's own music was
 * poorer than the card for ours beside it on the same rail.
 *
 * ## Why this is written here rather than installed
 *
 * Four fields, from four container formats, is a few hundred lines of byte walking. The libraries
 * that do this do far more — every format, every edge, every field — and weigh a couple of hundred
 * kilobytes for it. This app's own rules put mobile first and ask for the lightest correct
 * solution, and a tag reader is one of the rare things where "correct" is fully bounded: the
 * formats are the ones `LOCAL_SUPPORTED_EXTENSIONS` accepts, and the fields are the ones a card
 * shows. A dependency would also be a dependency, which is a decision that belongs to a person.
 *
 * ## What it reads
 *
 *   mp3, and any file with one at the front   ID3v2.2 / 2.3 / 2.4
 *   flac                                      Vorbis comments, and the picture block
 *   ogg, oga, opus                            Vorbis comments, in the Ogg comment header
 *   m4a, mp4, aac in an MP4                   the `moov/udta/meta/ilst` atoms
 *   wav                                       a `LIST`/`INFO` chunk, or an `id3 ` chunk
 *   webm, weba                                nothing — Matroska tags on audio are vanishingly
 *                                             rare, and EBML is a parser's worth of code for them
 *
 * ## The rules it holds to
 *
 * - **It never throws.** A file with no tags, half a tag, or a deliberately malformed one is not
 *   an error: it keeps its file name, exactly as every file did before this existed. Every parse
 *   is wrapped, every length is bounds-checked, and anything unrecognised returns nulls.
 * - **It reads no more than it must.** Tags live at a known place in every one of these formats —
 *   the front, or an atom the walk jumps between. Nothing scans a whole file.
 * - **The cover is opt-in.** Extracting it means copying image bytes out, and a caller that only
 *   wants the text should not pay for that. `picture: true` asks.
 */

/**
 * Which reader produced an answer.
 *
 * Stored beside the tags on an entry, so a library read by an older one is read again rather than
 * keeping its answer for ever — "this file says nothing" has to be re-askable, or the first reader
 * to miss something makes that miss permanent. Raise this whenever this file learns to read more.
 */
export const TAG_READER_VERSION = 1;

/** Beyond this, a "tag" is a broken length rather than a value. Nothing here is a novel. */
const MAX_TEXT_BYTES = 4096;

/** A cover larger than this is not stored, and not even copied out of the file. */
export const MAX_PICTURE_BYTES = 8 * 1024 * 1024;

/** What to read from a file whose front does not say how long its metadata is. */
const DEFAULT_HEAD_BYTES = 256 * 1024;

/** However long a tag claims to be, this is as much of a file as is ever read to find one. */
const MAX_HEAD_BYTES = 16 * 1024 * 1024;

const utf8 = new TextDecoder('utf-8', { fatal: false });
const latin1 = new TextDecoder('latin1', { fatal: false });
const utf16 = new TextDecoder('utf-16', { fatal: false });
const utf16be = new TextDecoder('utf-16be', { fatal: false });

/**
 * @typedef {object} AudioTags
 * @property {string|null} title
 * @property {string|null} artist
 * @property {string|null} album
 * @property {{ mimeType: string, bytes: Uint8Array }|null} picture the cover, when one was asked
 *   for and one is there
 */

/** Nothing was read. Its own function so every failure path returns the same shape. */
function noTags() {
  return { title: null, artist: null, album: null, picture: null };
}

/** A tag value, or null: whitespace and the empty string are not names. */
function clean(value) {
  if (typeof value !== 'string') return null;
  // Text frames are padded and terminated with NULs, and a reader that keeps them puts an
  // invisible character in the middle of somebody's artist name.
  const trimmed = value.replace(/\0+$/, '').trim();
  return trimmed ? trimmed : null;
}

function decodeText(bytes, encoding) {
  if (!bytes.length) return null;
  switch (encoding) {
    case 1:
      return clean(utf16.decode(bytes));
    case 2:
      return clean(utf16be.decode(bytes));
    case 3:
      return clean(utf8.decode(bytes));
    default:
      return clean(latin1.decode(bytes));
  }
}

/** The four ASCII characters at `at`, for an atom or chunk type. */
function fourCC(view, at) {
  if (at + 4 > view.byteLength) return '';
  return String.fromCharCode(view.getUint8(at), view.getUint8(at + 1), view.getUint8(at + 2), view.getUint8(at + 3));
}

/** ID3's synchsafe integer: seven bits per byte, so no byte can look like a frame sync. */
function synchsafe(view, at) {
  return (
    ((view.getUint8(at) & 0x7f) << 21) |
    ((view.getUint8(at + 1) & 0x7f) << 14) |
    ((view.getUint8(at + 2) & 0x7f) << 7) |
    (view.getUint8(at + 3) & 0x7f)
  );
}

// ── ID3v2 ────────────────────────────────────────────────────────────────────────────────────────

/** The frames worth reading, under all three of the id spellings ID3 has used. */
const ID3_FIELDS = {
  TIT2: 'title', TT2: 'title',
  TPE1: 'artist', TP1: 'artist',
  TALB: 'album', TAL: 'album',
  APIC: 'picture', PIC: 'picture',
};

function readId3Picture(bytes, version) {
  // <encoding><mime\0 | 3-char type><picture type><description\0><data>
  if (bytes.length < 4) return null;
  const encoding = bytes[0];
  let at = 1;
  let mimeType;
  if (version === 2) {
    // v2.2 has a three-character format instead of a MIME type.
    const format = latin1.decode(bytes.subarray(1, 4)).toLowerCase();
    mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
    at = 4;
  } else {
    const end = bytes.indexOf(0, at);
    if (end < 0) return null;
    mimeType = clean(latin1.decode(bytes.subarray(at, end))) || 'image/jpeg';
    at = end + 1;
  }
  at += 1; // the picture type byte: front cover, back cover, artist… we take whichever comes first
  // The description, terminated the way its own encoding terminates a string.
  if (encoding === 1 || encoding === 2) {
    while (at + 1 < bytes.length && !(bytes[at] === 0 && bytes[at + 1] === 0)) at += 2;
    at += 2;
  } else {
    const end = bytes.indexOf(0, at);
    if (end < 0) return null;
    at = end + 1;
  }
  if (at >= bytes.length) return null;
  const data = bytes.subarray(at);
  if (data.length > MAX_PICTURE_BYTES) return null;
  // A MIME that is not an image is a malformed frame, not a cover.
  return mimeType.startsWith('image/') ? { mimeType, bytes: data } : null;
}

function readId3(view, bytes, start, wantPicture) {
  const out = noTags();
  const version = view.getUint8(start + 3);
  if (version < 2 || version > 4) return out;
  const flags = view.getUint8(start + 5);
  const size = synchsafe(view, start + 6);
  const end = Math.min(start + 10 + size, bytes.length);
  let at = start + 10;
  // An extended header sits between the header and the first frame, and its own length is the
  // first thing in it — v2.4 counts itself in that length, v2.3 does not.
  if (flags & 0x40 && at + 4 <= end) {
    const extended = version === 4 ? synchsafe(view, at) : view.getUint32(at) + 4;
    at += Math.max(0, extended);
  }
  const idLength = version === 2 ? 3 : 4;
  const headerLength = version === 2 ? 6 : 10;
  while (at + headerLength <= end) {
    const id = String.fromCharCode(...bytes.subarray(at, at + idLength));
    // Padding: the tag is over, and the rest is zeroes to the audio.
    if (!/^[A-Z0-9]+$/.test(id)) break;
    const frameSize =
      version === 2
        ? (view.getUint8(at + 3) << 16) | (view.getUint8(at + 4) << 8) | view.getUint8(at + 5)
        : version === 4
          ? synchsafe(view, at + 4)
          : view.getUint32(at + 4);
    const body = at + headerLength;
    if (frameSize <= 0 || body + frameSize > end) break;
    const field = ID3_FIELDS[id];
    if (field === 'picture') {
      if (wantPicture && !out.picture) out.picture = readId3Picture(bytes.subarray(body, body + frameSize), version);
    } else if (field && !out[field]) {
      const frame = bytes.subarray(body, body + Math.min(frameSize, MAX_TEXT_BYTES));
      out[field] = decodeText(frame.subarray(1), frame[0]);
    }
    at = body + frameSize;
  }
  return out;
}

// ── Vorbis comments (FLAC and Ogg both) ──────────────────────────────────────────────────────────

const VORBIS_FIELDS = { TITLE: 'title', ARTIST: 'artist', ALBUM: 'album', ALBUMARTIST: 'artist' };

/**
 * The comment block itself: a vendor string, then `count` entries of `KEY=value` in UTF-8.
 * @param {Uint8Array} bytes starting at the vendor length
 */
function readVorbisComments(bytes, into) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 8) return into;
  const vendorLength = view.getUint32(0, true);
  let at = 4 + vendorLength;
  if (at + 4 > bytes.length) return into;
  const count = view.getUint32(at, true);
  at += 4;
  for (let i = 0; i < count && at + 4 <= bytes.length; i += 1) {
    const length = view.getUint32(at, true);
    at += 4;
    if (length <= 0 || at + length > bytes.length) break;
    if (length <= MAX_TEXT_BYTES) {
      const entry = utf8.decode(bytes.subarray(at, at + length));
      const split = entry.indexOf('=');
      if (split > 0) {
        const field = VORBIS_FIELDS[entry.slice(0, split).toUpperCase()];
        // ALBUMARTIST maps to artist too, but never over an ARTIST that was already read.
        if (field && !into[field]) into[field] = clean(entry.slice(split + 1));
      }
    }
    at += length;
  }
  return into;
}

function readFlac(view, bytes, wantPicture) {
  const out = noTags();
  let at = 4;
  // Metadata blocks, in order, until the one flagged last.
  while (at + 4 <= bytes.length) {
    const header = view.getUint8(at);
    const type = header & 0x7f;
    const length = (view.getUint8(at + 1) << 16) | (view.getUint8(at + 2) << 8) | view.getUint8(at + 3);
    const body = at + 4;
    if (length < 0 || body + length > bytes.length) break;
    if (type === 4) readVorbisComments(bytes.subarray(body, body + length), out);
    else if (type === 6 && wantPicture && !out.picture) out.picture = readFlacPicture(bytes.subarray(body, body + length));
    if (header & 0x80) break; // the last block
    at = body + length;
  }
  return out;
}

function readFlacPicture(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // <type><mime length><mime><description length><description><w><h><depth><colours><length><data>
  if (bytes.length < 32) return null;
  let at = 4;
  const mimeLength = view.getUint32(at);
  at += 4;
  if (at + mimeLength > bytes.length) return null;
  const mimeType = clean(latin1.decode(bytes.subarray(at, at + mimeLength))) || '';
  at += mimeLength;
  if (at + 4 > bytes.length) return null;
  const descriptionLength = view.getUint32(at);
  at += 4 + descriptionLength + 16;
  if (at + 4 > bytes.length) return null;
  const dataLength = view.getUint32(at);
  at += 4;
  if (dataLength <= 0 || at + dataLength > bytes.length || dataLength > MAX_PICTURE_BYTES) return null;
  return mimeType.startsWith('image/') ? { mimeType, bytes: bytes.subarray(at, at + dataLength) } : null;
}

/**
 * Ogg holds its comment header in the second logical packet, spread over pages whose payloads have
 * to be stitched back together. Only the first few pages are looked at: past those, this is audio.
 */
function readOgg(view, bytes, wantPicture) {
  const out = noTags();
  const payloads = [];
  let at = 0;
  let total = 0;
  for (let page = 0; page < 8 && at + 27 <= bytes.length; page += 1) {
    if (fourCC(view, at) !== 'OggS') break;
    const segments = view.getUint8(at + 26);
    const table = at + 27;
    if (table + segments > bytes.length) break;
    let length = 0;
    for (let i = 0; i < segments; i += 1) length += view.getUint8(table + i);
    const body = table + segments;
    if (body + length > bytes.length) break;
    payloads.push(bytes.subarray(body, body + length));
    total += length;
    at = body + length;
    // The comment header is the second packet; a couple of pages past it is a safe stop.
    if (total > 512 * 1024) break;
  }
  if (!payloads.length) return out;
  const joined = new Uint8Array(total);
  let written = 0;
  for (const payload of payloads) {
    joined.set(payload, written);
    written += payload.length;
  }
  const marker = findComment(joined);
  if (marker < 0) return out;
  readVorbisComments(joined.subarray(marker), out);
  if (wantPicture && !out.picture) {
    // Vorbis carries a cover as a base64 FLAC picture block in METADATA_BLOCK_PICTURE. Reading it
    // means decoding the whole block out of text, which is the one thing here that is not cheap —
    // so it is only done when a caller asked for a picture at all.
    out.picture = readVorbisPicture(joined.subarray(marker));
  }
  return out;
}

/** Where the vorbis comment structure starts inside the stitched packets, or -1. */
function findComment(bytes) {
  const vorbis = [0x03, 0x76, 0x6f, 0x72, 0x62, 0x69, 0x73]; // \x03 "vorbis"
  const opus = [0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73]; // "OpusTags"
  const limit = Math.min(bytes.length, 64 * 1024);
  for (let at = 0; at < limit; at += 1) {
    if (bytes[at] === 0x03 && vorbis.every((byte, i) => bytes[at + i] === byte)) return at + vorbis.length;
    if (bytes[at] === 0x4f && opus.every((byte, i) => bytes[at + i] === byte)) return at + opus.length;
  }
  return -1;
}

function readVorbisPicture(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 8) return null;
  const vendorLength = view.getUint32(0, true);
  let at = 4 + vendorLength;
  if (at + 4 > bytes.length) return null;
  const count = view.getUint32(at, true);
  at += 4;
  for (let i = 0; i < count && at + 4 <= bytes.length; i += 1) {
    const length = view.getUint32(at, true);
    at += 4;
    if (length <= 0 || at + length > bytes.length) break;
    // The key is ASCII, so it can be tested without decoding the megabyte of base64 behind it.
    const key = latin1.decode(bytes.subarray(at, at + Math.min(length, 24))).toUpperCase();
    if (key.startsWith('METADATA_BLOCK_PICTURE=') && length < MAX_PICTURE_BYTES * 2) {
      const base64 = latin1.decode(bytes.subarray(at + 'METADATA_BLOCK_PICTURE='.length, at + length));
      try {
        const binary = atob(base64);
        const block = new Uint8Array(binary.length);
        for (let b = 0; b < binary.length; b += 1) block[b] = binary.charCodeAt(b);
        return readFlacPicture(block);
      } catch {
        return null;
      }
    }
    at += length;
  }
  return null;
}

// ── MP4 / M4A ────────────────────────────────────────────────────────────────────────────────────

const MP4_FIELDS = {
  '©nam': 'title',
  '©ART': 'artist',
  '©alb': 'album',
  covr: 'picture',
};

/**
 * Walk the atoms of `[from, to)` and hand each one to `visit`, which says whether to descend.
 * Atoms are a size and a four-character type, and everything in an MP4 is one.
 */
function walkAtoms(view, from, to, visit) {
  let at = from;
  while (at + 8 <= to) {
    let size = view.getUint32(at);
    let header = 8;
    if (size === 1) {
      // A 64-bit size. Beyond 4 GB is not a tag, so only the low half is of any use here.
      if (at + 16 > to) return;
      const high = view.getUint32(at + 8);
      if (high !== 0) return;
      size = view.getUint32(at + 12);
      header = 16;
    } else if (size === 0) {
      size = to - at; // to the end of the file
    }
    if (size < header || at + size > to) return;
    visit(fourCC(view, at + 4), at + header, at + size);
    at += size;
  }
}

function readMp4(view, bytes, wantPicture) {
  const out = noTags();
  const readItem = (type, from, to) => {
    const field = MP4_FIELDS[type];
    if (!field) return;
    walkAtoms(view, from, to, (dataType, dataFrom, dataTo) => {
      if (dataType !== 'data' || dataFrom + 8 > dataTo) return;
      // <version+flags:4><reserved:4><payload>. The flags say what the payload is: 1 is text,
      // 13 a JPEG, 14 a PNG.
      const kind = view.getUint32(dataFrom) & 0xffffff;
      const body = bytes.subarray(dataFrom + 8, dataTo);
      if (field === 'picture') {
        if (!wantPicture || out.picture || body.length > MAX_PICTURE_BYTES) return;
        if (kind === 13 || kind === 14) out.picture = { mimeType: kind === 14 ? 'image/png' : 'image/jpeg', bytes: body };
        return;
      }
      if (!out[field] && kind === 1) out[field] = clean(utf8.decode(body.subarray(0, MAX_TEXT_BYTES)));
    });
  };
  walkAtoms(view, 0, bytes.length, (type, from, to) => {
    if (type !== 'moov') return;
    walkAtoms(view, from, to, (moovType, moovFrom, moovTo) => {
      if (moovType !== 'udta') return;
      walkAtoms(view, moovFrom, moovTo, (udtaType, udtaFrom, udtaTo) => {
        // `meta` is a full box: four bytes of version and flags before its children, which is the
        // one place in this format where an atom does not start where its header ends.
        if (udtaType !== 'meta' || udtaFrom + 4 > udtaTo) return;
        walkAtoms(view, udtaFrom + 4, udtaTo, (metaType, metaFrom, metaTo) => {
          if (metaType === 'ilst') walkAtoms(view, metaFrom, metaTo, readItem);
        });
      });
    });
  });
  return out;
}

// ── RIFF / WAVE ──────────────────────────────────────────────────────────────────────────────────

const RIFF_FIELDS = { INAM: 'title', IART: 'artist', IPRD: 'album' };

function readWav(view, bytes, wantPicture) {
  let out = noTags();
  let at = 12; // past 'RIFF' <size> 'WAVE'
  while (at + 8 <= bytes.length) {
    const type = fourCC(view, at);
    const size = view.getUint32(at + 4, true);
    const body = at + 8;
    if (size <= 0 || body + size > bytes.length) break;
    if (type === 'LIST' && fourCC(view, body) === 'INFO') {
      let item = body + 4;
      while (item + 8 <= body + size) {
        const itemType = fourCC(view, item);
        const itemSize = view.getUint32(item + 4, true);
        if (itemSize <= 0 || item + 8 + itemSize > body + size) break;
        const field = RIFF_FIELDS[itemType];
        if (field && !out[field]) {
          out[field] = clean(latin1.decode(bytes.subarray(item + 8, item + 8 + Math.min(itemSize, MAX_TEXT_BYTES))));
        }
        // Chunks are word-aligned: an odd length is followed by a pad byte.
        item += 8 + itemSize + (itemSize % 2);
      }
    } else if ((type === 'id3 ' || type === 'ID3 ') && fourCC(view, body).startsWith('ID3')) {
      // A whole ID3v2 tag, in a chunk. Same reader.
      const inner = readId3(view, bytes, body, wantPicture);
      out = { title: out.title ?? inner.title, artist: out.artist ?? inner.artist, album: out.album ?? inner.album, picture: inner.picture };
    }
    at = body + size + (size % 2);
  }
  return out;
}

// ── ID3v1, at the END of the file ────────────────────────────────────────────────────────────────

/** The fixed-width fields of the 128 bytes some files still carry after the audio. */
function readId3v1(tail) {
  if (tail.length < 128 || latin1.decode(tail.subarray(0, 3)) !== 'TAG') return null;
  // Every field is a fixed slice, space- or NUL-padded, in latin-1. There is nowhere for a cover.
  return {
    title: clean(latin1.decode(tail.subarray(3, 33))),
    artist: clean(latin1.decode(tail.subarray(33, 63))),
    album: clean(latin1.decode(tail.subarray(63, 93))),
    picture: null,
  };
}

/** Fill in what the container did not say, from a tag that only has three fields to give. */
function withId3v1(tags, tail) {
  if (tags.title && tags.artist) return tags;
  const old = readId3v1(tail);
  if (!old) return tags;
  return {
    title: tags.title ?? old.title,
    artist: tags.artist ?? old.artist,
    album: tags.album ?? old.album,
    picture: tags.picture,
  };
}

// ── The door ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Read what a file says about itself.
 *
 * Never throws and never rejects: a file that says nothing, or says it wrongly, comes back as
 * nulls and keeps its file name.
 *
 * @param {ArrayBuffer|Uint8Array|Blob} source the file, or as much of its front as the caller has
 * @param {{ picture?: boolean }} [options] whether to copy the cover out, which a caller that only
 *   wants the text should not pay for
 * @returns {Promise<AudioTags>}
 */
export async function readAudioTags(source, { picture = false } = {}) {
  try {
    let bytes;
    if (source instanceof Uint8Array) bytes = source;
    else if (source instanceof ArrayBuffer) bytes = new Uint8Array(source);
    else if (source && typeof source.arrayBuffer === 'function') bytes = new Uint8Array(await source.arrayBuffer());
    else return noTags();
    // Below this there is not even a container header to recognise.
    if (bytes.length < 12) return noTags();
    const tags = readHead(bytes, picture);
    // This entry point is given a WHOLE file, so the last 128 bytes really are its end.
    return bytes.length >= 128 ? withId3v1(tags, bytes.subarray(bytes.length - 128)) : tags;
  } catch (error) {
    // A malformed file is a file with no tags. It still plays, and it still has a name.
    console.warn('[local] could not read the tags of a file', error);
    return noTags();
  }
}

/**
 * Read the container at the front of `bytes`. The old 128-byte tag at the END of a file is NOT
 * read here: this is handed whatever slice a caller has, and the last 128 bytes of a head slice
 * are audio. Bytes that happened to start with "TAG" would decode a title out of the middle of
 * the music, and `withId3v1` fills only empty fields, so nothing later could correct it. Each
 * entry point applies the tail itself, from bytes it knows are the real end of the file.
 */
function readHead(bytes, wantPicture) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const head = fourCC(view, 0);
  const tags = head.startsWith('ID3')
    ? readId3(view, bytes, 0, wantPicture)
    : head === 'fLaC'
      ? readFlac(view, bytes, wantPicture)
      : head === 'OggS'
        ? readOgg(view, bytes, wantPicture)
        : head === 'RIFF' && fourCC(view, 8) === 'WAVE'
          ? readWav(view, bytes, wantPicture)
          : fourCC(view, 4) === 'ftyp'
            ? readMp4(view, bytes, wantPicture)
            : noTags();
  return tags;
}

/**
 * Read the tags of a file without reading the file.
 *
 * The front of every one of these formats says how long its own metadata is, so this asks for
 * exactly that much and no more — which matters, because an ID3 tag with a cover and an
 * encapsulated object in it is routinely several megabytes, and a fixed guess either truncates
 * those or reads a whole album for the files that have none.
 *
 * @param {Blob} blob the file
 * @param {{ picture?: boolean }} [options]
 * @returns {Promise<AudioTags>}
 */
export async function readAudioTagsFromBlob(blob, { picture = false } = {}) {
  try {
    if (!blob || typeof blob.slice !== 'function' || blob.size < 12) return noTags();
    const front = new Uint8Array(await blob.slice(0, 10).arrayBuffer());
    let wanted = DEFAULT_HEAD_BYTES;
    if (latin1.decode(front.subarray(0, 3)) === 'ID3') {
      const view = new DataView(front.buffer, front.byteOffset, front.byteLength);
      // The tag says its own length. A little more, for the frame that starts right after it.
      wanted = Math.min(10 + synchsafe(view, 6) + 1024, MAX_HEAD_BYTES);
    }
    const head = new Uint8Array(await blob.slice(0, Math.min(wanted, blob.size)).arrayBuffer());
    const tags = readHead(head, picture);
    if (tags.title && tags.artist) return tags;
    // The front said nothing, or not everything. The old tag lives in the last 128 bytes, and
    // fetching those is the cheapest read in this file.
    const tail = new Uint8Array(await blob.slice(Math.max(0, blob.size - 128), blob.size).arrayBuffer());
    return withId3v1(tags, tail);
  } catch (error) {
    console.warn('[local] could not read the tags of a file', error);
    return noTags();
  }
}
